import { z } from 'zod';
import { E164, Id, PageQuery, Timestamp } from './common.js';
import { Email } from './auth.js';

export const Contact = z.object({
  id: Id,
  name: z.string().min(1),
  designation: z.string().nullable(),
  phones: z.array(E164),
  emails: z.array(Email),
  langPref: z.string().nullable(),
  channelPref: z.string().nullable(),
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Contact = z.infer<typeof Contact>;

export const CreateContactRequest = z
  .object({
    name: z.string().trim().min(1).max(200),
    designation: z.string().trim().max(200).optional(),
    phones: z.array(E164).max(10).default([]),
    emails: z.array(Email).max(10).default([]),
    langPref: z.string().max(40).optional(),
    channelPref: z.string().max(40).optional(),
    /** Must be true to create despite dedupe matches returned by /contacts/dedupe. */
    confirmNotDuplicate: z.boolean().default(false),
  })
  .strict();
export type CreateContactRequest = z.infer<typeof CreateContactRequest>;

export const UpdateContactRequest = CreateContactRequest.omit({ confirmNotDuplicate: true })
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateContactRequest = z.infer<typeof UpdateContactRequest>;

export const ContactListQuery = PageQuery.extend({ q: z.string().trim().max(200).optional() });
export type ContactListQuery = z.infer<typeof ContactListQuery>;

export const DedupeQuery = z
  .object({
    name: z.string().trim().max(200).optional(),
    phone: E164.optional(),
    email: Email.optional(),
  })
  .refine((v) => v.name || v.phone || v.email, 'give at least one of name, phone, email');
export type DedupeQuery = z.infer<typeof DedupeQuery>;

export const DedupeMatch = z.object({
  contact: Contact,
  matchedOn: z.array(z.enum(['phone', 'email', 'name'])).min(1),
  score: z.number().min(0).max(1),
});
export const DedupeResponse = z.object({ matches: z.array(DedupeMatch) });
export type DedupeResponse = z.infer<typeof DedupeResponse>;

export const Account = z.object({
  id: Id,
  company: z.string().min(1),
  division: z.string().nullable(),
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Account = z.infer<typeof Account>;

export const CreateAccountRequest = z
  .object({ company: z.string().trim().min(1).max(200), division: z.string().trim().max(200).optional() })
  .strict();
export type CreateAccountRequest = z.infer<typeof CreateAccountRequest>;
export const UpdateAccountRequest = CreateAccountRequest.partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateAccountRequest = z.infer<typeof UpdateAccountRequest>;
export const AccountListQuery = ContactListQuery;
